import { useRef, useState } from "react";
import { Check, ImageUp } from "lucide-react";
import { assetService } from "../../assets";
import type { EditorAsset } from "../editor.types";
import { MEDIA_DRAG_TYPE } from "./mediaUsage";

/** The grid's columns, shared with the Media section's folders so they line up. */
export const mediaTilesClass = "grid grid-cols-3 gap-2 min-[420px]:grid-cols-4";

/**
 * The site's images as a grid of square thumbnails, newest first, led by a
 * tile to upload more. Shared by the Media section and the image picker.
 */
export function MediaGrid({
  assets,
  selected = [],
  onPick,
  onUpload,
  label,
  draggable = false,
  empty = "Images you upload appear here, ready to use anywhere on your site.",
}: {
  assets: EditorAsset[];
  /** Ids shown as chosen. */
  selected?: string[];
  onPick: (asset: EditorAsset) => void;
  /** Uploads each file; resolves once they're all in. */
  onUpload: (files: File[]) => Promise<void>;
  label: string;
  /** Lets images be dragged onto the page (the Media section). */
  draggable?: boolean;
  /** Shown while there are no images. */
  empty?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const images = assets
    .filter((asset) => !asset.mime_type || asset.mime_type.startsWith("image/"))
    .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
  const upload = async (files: File[]) => {
    const pictures = files.filter((file) => file.type.startsWith("image/"));
    if (!pictures.length) return setError("Choose an image file.");
    setUploading(true);
    setError("");
    try {
      await onUpload(pictures);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "Could not upload. Try again.");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="relative grid gap-2">
      <div className={mediaTilesClass} role="listbox" aria-label={label} aria-multiselectable={selected.length > 1 || undefined}>
        <button
          type="button"
          disabled={uploading}
          className="grid aspect-square place-content-center justify-items-center gap-1.5 rounded-xl border border-dashed border-hazumu-clay text-xs text-hazumu-ink transition-colors hover:bg-hazumu-blush disabled:opacity-60"
          onClick={() => input.current?.click()}
        >
          <ImageUp size={18} aria-hidden="true" />
          {uploading ? "Uploading…" : "Upload"}
        </button>
        {images.map((asset) => {
          const chosen = selected.includes(asset.id);
          return (
            <button
              key={asset.id}
              type="button"
              role="option"
              aria-selected={chosen}
              aria-label={asset.alt_text || "Image"}
              draggable={draggable}
              onDragStart={(event) => {
                event.dataTransfer.setData(MEDIA_DRAG_TYPE, asset.id);
                event.dataTransfer.effectAllowed = "copy";
                const image = event.currentTarget.querySelector("img");
                if (image) event.dataTransfer.setDragImage(image, 24, 24);
              }}
              className="relative aspect-square overflow-hidden rounded-xl bg-hazumu-canvas ring-hazumu-terracotta ring-offset-2 transition-shadow focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink aria-selected:ring-2"
              onClick={() => onPick(asset)}
            >
              <img src={assetService.getPublicUrl(asset.storage_path)} alt="" loading="lazy" draggable={false} className="size-full object-cover" />
              {chosen && (
                <span className="absolute top-1.5 right-1.5 grid size-5 place-items-center rounded-full bg-hazumu-terracotta text-hazumu-white" aria-hidden="true">
                  <Check size={12} strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
      </div>
      {!images.length && <p className="text-sm text-hazumu-muted">{empty}</p>}
      {error && <p role="alert" className="text-xs text-hazumu-danger">{error}</p>}
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
        multiple
        aria-label="Upload images"
        className="sr-only"
        tabIndex={-1}
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          event.target.value = "";
          if (files.length) void upload(files);
        }}
      />
    </div>
  );
}
